import { getNotificationLookupTypes, collapseChosenStoredType } from './notificationTopics';
import { uiT } from './uiTranslate';

export const NOTIFICATION_CHANNEL_IN_APP = 'in_app';
export const NOTIFICATION_CHANNEL_EMAIL = 'email';

export const NOTIFICATION_CHANNEL_OPTIONS = [
  { key: NOTIFICATION_CHANNEL_IN_APP, get label() { return uiT('Common.NotificationChannel.InApp'); } },
  { key: NOTIFICATION_CHANNEL_EMAIL, get label() { return uiT('Common.NotificationChannel.Email'); } },
];

/** Staré hodnoty z DB (OTK/údržba) – v novej logike znamenajú len in_app, nie email; email len explicitný kľúč "email". */
const LEGACY_IN_APP_VALUES = new Set(['Nový záznam', 'Nová', 'Zmena stavu']);
let lastOpenedNotificationType = '';

/** Nastaví kontext pre ChosenNotificationsDialog (názov topicu) bez otvorenia záznamu z API. */
export function primeNotificationDialogPageType(pageOrType) {
  lastOpenedNotificationType = String(pageOrType ?? '').trim();
}

/** Načítanie uložených preferencií zoznamu priorít (legacy pole ID alebo { departments, channels }). */
export function parsePriorityListChosenData(rawDataString) {
  const defaultChannels = [NOTIFICATION_CHANNEL_IN_APP, NOTIFICATION_CHANNEL_EMAIL];
  if (rawDataString == null || rawDataString === '') {
    return { departments: [], channels: defaultChannels };
  }
  try {
    const parsed = JSON.parse(rawDataString);
    if (Array.isArray(parsed)) {
      const departments = parsed
        .map((x) => (typeof x === 'number' ? x : parseInt(String(x), 10)))
        .filter((n) => !Number.isNaN(n));
      return { departments, channels: defaultChannels };
    }
    if (parsed && typeof parsed === 'object' && Array.isArray(parsed.departments)) {
      const departments = parsed.departments
        .map((x) => (typeof x === 'number' ? x : parseInt(String(x), 10)))
        .filter((n) => !Number.isNaN(n));
      const normalized = normalizeChosenNotifications(parsed.channels);
      const channels = normalized.length > 0 ? normalized : defaultChannels;
      return { departments, channels };
    }
  } catch (_) {
    /* ignore */
  }
  return { departments: [], channels: defaultChannels };
}

export function buildPriorityListChosenPayload(departmentIds, channelKeys) {
  return {
    departments: (Array.isArray(departmentIds) ? departmentIds : [])
      .map((x) => (typeof x === 'number' ? x : parseInt(String(x), 10)))
      .filter((n) => !Number.isNaN(n)),
    channels: normalizeChosenNotifications(channelKeys),
  };
}

export function savePriorityListChosenNotifications({
  type,
  departments,
  channels,
  postRequest,
  dispatch,
  toast,
  closeDialog,
}) {
  const storedType = collapseChosenStoredType(type);
  const payload = buildPriorityListChosenPayload(departments, channels);
  const formData = new FormData();
  formData.append('type', storedType);
  formData.append('data', JSON.stringify(payload));
  postRequest('/api/chosen_notifications', formData, true)
    .then((response) => {
      if (response.status === 201) {
        dispatch({ type: 'load_chosen_notifications', newChosenNotifications: response.data });
        toast?.current?.show({ severity: 'info', summary: uiT('Common.Confirmed'), life: 3000, detail: uiT('PriorityList.Toast.StateUpdated') });
      } else {
        toast?.current?.show({ severity: 'warn', summary: uiT('Common.Rejected'), detail: uiT('Common.Toast.RequestFailedExecute'), life: 3000 });
      }
    })
    .catch((reason) => {
      console.log(reason);
      alert(uiT('Common.Toast.ActionFailedDetail'));
    })
    .finally(() => {
      closeDialog?.();
    });
}

export function normalizeChosenNotifications(rawValue) {
  const value = Array.isArray(rawValue) ? rawValue : [];
  const next = new Set();
  value.forEach((item) => {
    const v = String(item ?? '').trim();
    if (!v) return;
    if (v === NOTIFICATION_CHANNEL_IN_APP || v === NOTIFICATION_CHANNEL_EMAIL) {
      next.add(v);
      return;
    }
    // Backward compatibility for older saved values.
    if (LEGACY_IN_APP_VALUES.has(v)) {
      next.add(NOTIFICATION_CHANNEL_IN_APP);
    }
  });
  return Array.from(next);
}

/**
 * Načítanie kanálov z uloženého JSON (PC): nový tvar { channels: [...] } alebo legacy ploché pole.
 * @param {*} parsed – výsledok JSON.parse(db.data)
 * @returns {string[]}
 */
export function parseChannelOnlyChosenPayload(parsed) {
  if (parsed == null) {
    return [];
  }
  if (Array.isArray(parsed)) {
    return normalizeChosenNotifications(parsed);
  }
  if (typeof parsed === 'object' && Array.isArray(parsed.channels)) {
    return normalizeChosenNotifications(parsed.channels);
  }
  return [];
}

/** Uloženie kanálov pre moduly bez extra polí (údržba, OTK, nastavenia priorít, …). */
export function buildChannelOnlyPayload(chosenNotifications) {
  return {
    channels: normalizeChosenNotifications(chosenNotifications),
  };
}

/**
 * @param {{ dataMode?: 'channels' | 'departmentIds' }} [options]
 *   departmentIds – zoznam priorít: pole ID oddelení (čísla), bez normalizácie na in_app/email
 */
export function openChosenNotifications(state, type, setChosenNotifications, setNotificationVisible, options = {}) {
  lastOpenedNotificationType = String(type ?? '').trim();
  const lookupTypes = getNotificationLookupTypes(type);
  const db = (state?.chosen_notifications || []).find((item) => lookupTypes.includes(item.type));
  if (db?.data) {
    try {
      const parsed = JSON.parse(db.data);
      if (options.dataMode === 'departmentIds') {
        const ids = Array.isArray(parsed)
          ? parsed
            .map((x) => (typeof x === 'number' ? x : parseInt(String(x), 10)))
            .filter((n) => !Number.isNaN(n))
          : [];
        setChosenNotifications(ids);
      } else {
        setChosenNotifications(parseChannelOnlyChosenPayload(parsed));
      }
    } catch (_) {
      setChosenNotifications([]);
    }
  } else {
    setChosenNotifications([]);
  }
  setNotificationVisible(true);
}

export function getLastOpenedNotificationType() {
  return lastOpenedNotificationType;
}

export function saveChosenNotifications({
  type,
  chosenNotifications,
  postRequest,
  dispatch,
  toast,
  closeDialog,
  skipNormalize = false,
}) {
  const storedType = collapseChosenStoredType(type);
  const formData = new FormData();
  formData.append('type', storedType);
  let payload;
  if (skipNormalize) {
    payload =
      chosenNotifications != null && typeof chosenNotifications === 'object' && !Array.isArray(chosenNotifications)
        ? chosenNotifications
        : Array.isArray(chosenNotifications)
          ? chosenNotifications
          : [];
  } else {
    payload = buildChannelOnlyPayload(chosenNotifications);
  }
  formData.append('data', JSON.stringify(payload));
  postRequest('/api/chosen_notifications', formData, true)
    .then((response) => {
      if (response.status === 201) {
        dispatch({ type: 'load_chosen_notifications', newChosenNotifications: response.data });
        toast?.current?.show({ severity: 'info', summary: uiT('Common.Confirmed'), life: 3000, detail: uiT('PriorityList.Toast.StateUpdated') });
      } else {
        toast?.current?.show({ severity: 'warn', summary: uiT('Common.Rejected'), detail: uiT('Common.Toast.RequestFailedExecute'), life: 3000 });
      }
    })
    .catch((reason) => {
      console.log(reason);
      alert(uiT('Common.Toast.ActionFailedDetail'));
    })
    .finally(() => {
      closeDialog?.();
    });
}
